const user = (window.app && window.app.user) || {}
const allowedActivities = user.allowed_activities || []
// NOTE: we might not have a user on some pages, in which case this doesn't matter anyway
const free = user.access_level?.free

export const menu = {
  grow: {
    title: "Grow",
    links: [
      {
        href: "/subscribers",
        label: "Subscribers",
        content: "Browse all your subscribers and customers.",
        icon: "three-people",
        iconColor: "text-black"
      },
      {
        label: "Creator Profile",
        href: "/creator_profile",
        content:
          "Your hub on the internet. A one-stop-shop for your subscribers to find all your content & products.",
        icon: "personalization",
        iconColor: "text-black"
      },
      {
        label: "Landing Pages & Forms",
        href: "/forms",
        content:
          "Gather subscribers with a hosted landing page or a form embedded on your website.",
        icon: "landing-page",
        iconColor: "text-black"
      },
      {
        label: "Recommendations",
        href: "/creator-network",
        isPaidFeature: true,
        canUseFeature: allowedActivities.includes("recommendations"),
        content:
          "Join the Creator Network to cross-promote your work with other creators and grow your email lists together.",
        icon: "recommendation-reciprocated",
        iconColor: "text-black"
      }
    ]
  },
  send: {
    title: "Send",
    links: [
      {
        href: "/campaigns",
        label: "Broadcasts",
        content:
          "Send an email to some, or all, of your audience, and to your public feed.",
        icon: "megaphone",
        iconColor: "text-black"
      },
      {
        href: "/account/email_layout_templates",
        label: "Email Templates",
        content:
          "Create email templates to use as a starting point in your emails.",
        icon: "template",
        iconColor: "text-black"
      },
      {
        href: "/sequences",
        label: "Sequences",
        content: "Create multiple emails that are sent in order.",
        icon: "emails",
        isPaidFeature: true,
        canUseFeature: allowedActivities.includes("sequences"),
        iconColor: "text-black"
      },
      {
        href: "/account/snippets",
        label: "Snippets",
        content: "Supercharge your emails with reusable blocks of content.",
        icon: "cube",
        iconColor: "text-black"
      }
    ]
  },
  automate: {
    title: "Automate",
    links: [
      {
        href: "/automations",
        label: "Visual Automations",
        content: "Create powerful, automated funnels for your audience.",
        icon: "bolt",
        isPaidFeature: true,
        canUseFeature: allowedActivities.includes("va"),
        iconColor: "text-black"
      },
      {
        href: "/apps",
        label: "Apps",
        content:
          "Power up your account by connecting with your favorite apps and integrations.",
        icon: "plug",
        iconColor: "text-black",
        // TODO: check canUseFeature and isPaidFeature when free apps project is complete
        canUseFeature: allowedActivities.includes("apps"),
        isPaidFeature: true
      },
      {
        href: "/rules",
        label: "Rules",
        content: '"If this, then that" automation rules.',
        icon: "rules",
        iconColor: "text-black",
        canUseFeature: allowedActivities.includes("rules"),
        isPaidFeature: true
      },
      {
        href: "/kit-mcp",
        label: "Kit MCP",
        content: "Connect your Kit account to your favorite AI tool.",
        icon: "sparkle",
        iconColor: "text-black",
        showBetaBadge: true
      },
      {
        href: "/feeds",
        label: "RSS",
        content:
          "Automagically generate emails or digests when RSS feeds update.",
        icon: "rss",
        canUseFeature: allowedActivities.includes("rss"),
        isPaidFeature: true,
        iconColor: "text-black"
      }
    ]
  },
  earn: {
    title: "Earn",
    links: [
      {
        href: "/products",
        label: "Products",
        content:
          "Sell digital products and content subscriptions directly to your audience.",
        icon: "bag",
        iconColor: "text-black"
      },
      {
        href: "/paid_recommendations",
        label: "Paid Recommendations",
        content: "Get paid to recommend other creators in the Creator Network.",
        icon: "rec-paid",
        isPaidFeature: true,
        canUseFeature:
          !free && allowedActivities.includes("paid_recommendations"),
        iconColor: "text-black"
      },
      {
        href: "/ads",
        label: "Newsletter Sponsorships",
        content:
          "Connect with brands to monetize your newsletter with sponsorships.",
        icon: "ad",
        canUseFeature: allowedActivities.includes("ads"),
        iconColor: "text-black",
        showBetaBadge: true
      },
      {
        href: "/tips",
        label: "Tips",
        content: "Give your audience a simple way to support your work.",
        icon: "tip-jar",
        iconColor: "text-black"
      },
      {
        href: "/account_settings/commerce/#payouts",
        label: "Payouts",
        content: "See a breakdown of all payouts you’ve received.",
        icon: "wallet",
        iconColor: "text-black"
      }
    ]
  },
  help: {
    title: "Help",
    links: [
      {
        label: "AI Assistant",
        href: "https://help.kit.com/",
        content: "Get instant answers about your Kit account anytime.",
        icon: "sparkle",
        iconColor: "text-black",
        onClick: () => {
          /* eslint-disable-next-line no-undef */
          if (typeof Intercom === "undefined" || !Intercom) {
            window.open("https://help.kit.com/", "_blank")
            return
          }
          /* eslint-disable-next-line no-undef */
          Intercom("showNewMessage")
        }
      },
      {
        href: "https://help.kit.com/",
        label: "Documentation",
        content: "Step-by-step guides for using every Kit feature.",
        icon: "book",
        iconColor: "text-black",
        newTab: true
      },
      {
        href: "/community",
        label: "Community",
        content: "Connect with creators who get what you're building.",
        icon: "chat-conversation",
        iconColor: "text-black",
        newTab: true
      },
      {
        href: "https://learn.kit.com/courses",
        label: "Creator University",
        content:
          "In-depth courses to help you earn a living as a creator with Kit.",
        icon: "education",
        iconColor: "text-black",
        newTab: true
      }
    ]
  }
}

export const settings = (isAdmin, { accountId = 0, accounts = [] }) => {
  const accountUsers = Array.isArray(accounts) ? accounts : []
  const admin = !isAdmin
    ? {}
    : {
        admin: {
          icon: "profile",
          label: "Admin",
          color: "text-black",
          href: "/admin"
        },
        viewInAdmin: {
          icon: "view",
          label: "View account in admin",
          color: "text-black",
          href: `/admin/accounts/${accountId}`
        }
      }

  const getAccessLabel = accountData => {
    if (accountData.unpaid) {
      return "[no access, unpaid] "
    } else if (accountData.trial_expired) {
      return "[no access, trial expired] "
    }

    return ""
  }

  const switchUsers =
    accountUsers.length < 1
      ? {}
      : {
          accounts: {
            label: "Switch Creator Accounts",
            method: "post",
            color: "text-gray-900",
            data: accountUsers
              .map(account => ({
                ...account,
                label: `${getAccessLabel(account)}${
                  account.name || account.email
                }`,
                href: `/account_users/${account.id}/switch`
              }))
              .sort((a, b) => a.label.localeCompare(b.label))
          }
        }

  const settings = {
    settings: {
      icon: "settings-cog",
      label: "Settings",
      color: "text-black",
      href: "/account_settings/account_info"
    }
  }

  return {
    ...admin,
    ...settings,
    help: {
      icon: "support",
      label: "Help",
      color: "text-black",
      href: "mailto:help@kit.com",
      newTab: true
    },
    ...switchUsers,
    logout: {
      icon: "log-out",
      label: "Log out",
      color: "text-gray-800",
      href: "/users/logout",
      method: "delete"
    }
  }
}
